import React, { useState, useRef, useEffect } from 'react'; // useEffect eklendi
import AdUnit from '../components/AdUnit';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';

// Get site domain from environment variables
const SITE_DOMAIN = process.env.REACT_APP_SITE_DOMAIN || 'https://yourjsontools.com';

const JsonToSchemaPage = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [schemaOutput, setSchemaOutput] = useState('');
  const [error, setError] = useState('');
  const [schemaVersion, setSchemaVersion] = useState('draft-2020-12'); // Default to latest
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isInputScrolling, setIsInputScrolling] = useState(false);
  const [isOutputScrolling, setIsOutputScrolling] = useState(false);

  // Maximum allowed line count for processing
  const MAX_LINE_COUNT = 20000;

  const initialSchemaSettings = {
    required: true, includeDefaults: true, includeExamples: true, strictTypes: false,
    additionalProperties: false, arrayValidation: true, stringValidation: true,
    numberValidation: true, includeTitle: true, includeDescription: true,
    enumValidation: false, minMaxProperties: false, formatValidation: true,
    patternProperties: false, dependencies: false // 'dependencies' is deprecated in 2020-12
  };
  const [tempSettings, setTempSettings] = useState({ ...initialSchemaSettings });
  const [schemaSettings, setSchemaSettings] = useState({ ...initialSchemaSettings });
  
  const fileInputRef = useRef(null);
  const jsonInputRef = useRef(null); // Textarea için ref
  const schemaOutputRef = useRef(null); // Pre için ref
  const jsonInputLineNumbersRef = useRef(null); // Sol satır numaraları için ref
  const schemaOutputLineNumbersRef = useRef(null); // Sağ satır numaraları için ref

  const exampleJson = {
    person: {
      name: "John Doe", age: 30, email: "john@example.com", isEmployed: true,
      address: { street: "123 Main St", city: "Anytown", zipCode: "12345" },
      phoneNumbers: [{ type: "home", number: "555-1234" }, { type: "work", number: "555-5678" }],
      tags: ["developer", "javascript", "web"], height: 182.5,
      dateOfBirth: "1992-05-15T00:00:00.000Z", nullValue: null
    }
  };

  const schemaVersions = [
    { id: 'draft-cs', name: 'Draft-cs', url: 'http://json-schema.org/draft-07/schema', customValidations: ['$schema', '$id', 'title', 'description', 'type', 'examples', 'required', 'additionalProperties', 'properties', 'items', 'additionalItems'] },
    { id: 'draft-2020-12', name: 'Draft 2020-12', url: 'https://json-schema.org/draft/2020-12/schema' },
    { id: 'draft-07', name: 'Draft-07', url: 'http://json-schema.org/draft-07/schema' },
    { id: 'draft-06', name: 'Draft-06', url: 'http://json-schema.org/draft-06/schema' },
    { id: 'draft-04', name: 'Draft-04', url: 'http://json-schema.org/draft-04/schema' },
    { id: 'draft-03', name: 'Draft-03', url: 'http://json-schema.org/draft-03/schema' }
  ];
  
  const getJsonType = (value) => {
    if (value === null) return 'null';
    const type = typeof value;
    if (type === 'number') {
      return Number.isInteger(value) ? 'integer' : 'number';
    }
    if (Array.isArray(value)) return 'array';
    return type;
  };

  const detectStringFormat = (value) => {
    if (typeof value !== 'string') return null;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/.test(value)) return 'date-time';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'date';
    if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) return 'email';
    if (/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/.test(value)) return 'uri';
    if (/^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(value)) return 'uuid';
    if (/^(\d{1,3}\.){3}\d{1,3}$/.test(value)) return 'ipv4';
    if (/^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/.test(value)) return 'ipv6';
    return null;
  };
  
  const allElementsUnique = (arr) => {
    if (!Array.isArray(arr)) return false;
    const primitiveValues = arr.filter(item => typeof item !== 'object' || item === null);
    return new Set(primitiveValues).size === primitiveValues.length;
  };

  const generateJsonSchema = (json, currentVersionId) => {
    const schemaVersionInfo = schemaVersions.find(v => v.id === currentVersionId) || schemaVersions[0];
    const schema = { $schema: schemaVersionInfo.url };
    
    // Add $id for draft-cs
    if (currentVersionId === 'draft-cs' && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('$id'))) {
      schema.$id = `http://example.com/example.json`;
    }
    
    if (schemaSettings.includeTitle && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('title'))) {
      if (currentVersionId === 'draft-cs') {
        schema.title = "Root Schema";
      } else {
        schema.title = "Generated Schema";
      }
    }
    
    if (schemaSettings.includeDescription && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('description'))) {
      if (currentVersionId === 'draft-cs') {
        schema.description = "Schema generated from JSON data";
      } else {
        schema.description = "Schema generated from JSON data";
      }
    }

    const buildPropertiesRecursive = (currentJson, currentSchemaNode, propertyName = null) => {
        const type = getJsonType(currentJson);
        currentSchemaNode.type = type;
        
        // Add title for draft-cs
        if (currentVersionId === 'draft-cs' && propertyName && schemaSettings.includeTitle && 
            (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('title'))) {
          currentSchemaNode.title = `The ${propertyName} Schema`;
        }

        if (type === 'object' && currentJson !== null) {
            if (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('properties')) {
              currentSchemaNode.properties = {};
            }
            
            if (schemaSettings.required && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('required'))) {
              currentSchemaNode.required = [];
            }
            
            // 'additionalProperties' is generally applicable
            if (currentVersionId !== 'draft-03' && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('additionalProperties'))) {
                currentSchemaNode.additionalProperties = schemaSettings.additionalProperties;
            }

            if (schemaSettings.minMaxProperties && (!schemaVersionInfo.customValidations || !schemaVersionInfo.customValidations.includes('minProperties'))) {
                 const numKeys = Object.keys(currentJson).length;
                 currentSchemaNode.minProperties = numKeys > 0 ? 1 : 0;
                 currentSchemaNode.maxProperties = numKeys;
            }
            
            if (currentSchemaNode.properties) {
              for (const key in currentJson) {
                  if (Object.prototype.hasOwnProperty.call(currentJson, key)) {
                      currentSchemaNode.properties[key] = {};
                      buildPropertiesRecursive(currentJson[key], currentSchemaNode.properties[key], key);
                      if (schemaSettings.required && currentJson[key] !== null && currentJson[key] !== undefined && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('required'))) {
                          if (!currentSchemaNode.required) currentSchemaNode.required = [];
                          currentSchemaNode.required.push(key);
                      }
                  }
              }
            }
            
            if (currentSchemaNode.required && currentSchemaNode.required.length === 0) {
                delete currentSchemaNode.required;
            }
            
            // Add examples for objects in draft-cs
            if (currentVersionId === 'draft-cs' && schemaSettings.includeExamples && 
                (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('examples'))) {
                currentSchemaNode.examples = [currentJson];
            }
            
            // Note: 'dependencies' keyword is deprecated in Draft 2020-12.
            // This generator does not currently produce 'dependencies', 'dependentSchemas', or 'dependentRequired' from input.
            // If schemaSettings.dependencies were true, logic would be needed here for older drafts vs 2020-12.
        } else if (type === 'array' && currentJson !== null) {
            if (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('items')) {
              currentSchemaNode.items = {}; 
            }
            
            if (currentSchemaNode.items && currentJson.length > 0) {
                const itemTypes = new Set(currentJson.map(item => getJsonType(item)));
                if (itemTypes.size === 1) {
                    const itemType = Array.from(itemTypes)[0];
                    const itemSchemaBoilerplate = {}; 
                    buildPropertiesRecursive(currentJson[0], itemSchemaBoilerplate, 'A Schema');
                    currentSchemaNode.items = itemSchemaBoilerplate;

                    if (schemaSettings.enumValidation && itemType !== 'object' && itemType !== 'array' && itemType !== 'null' && 
                        (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('enum'))) {
                        const uniqueValues = Array.from(new Set(currentJson.filter(v => v !== null && typeof v !== 'object')));
                        if (uniqueValues.length > 1 && uniqueValues.length <= 10) { 
                           if (typeof currentSchemaNode.items.type === 'undefined') { 
                                currentSchemaNode.items = { type: itemType }; 
                           }
                           currentSchemaNode.items.enum = uniqueValues;
                        }
                    }
                } else if (itemTypes.size > 1) { 
                    currentSchemaNode.items = {
                        oneOf: Array.from(itemTypes).map(t => {
                            const sampleItemOfType = currentJson.find(item => getJsonType(item) === t);
                            const itemSchemaBoilerplate = {};
                            buildPropertiesRecursive(sampleItemOfType, itemSchemaBoilerplate, 'A Schema');
                            return itemSchemaBoilerplate;
                        })
                    };
                }
                
                // Handle mixed types for arrays with objects that have different property types
                if (currentSchemaNode.items && currentSchemaNode.items.properties) {
                    const allItems = currentJson;
                    for (const [propKey, propSchema] of Object.entries(currentSchemaNode.items.properties)) {
                        const allValuesForProp = allItems.map(item => item[propKey]).filter(val => val !== undefined);
                        const uniqueTypes = new Set(allValuesForProp.map(val => getJsonType(val)));
                        
                        if (uniqueTypes.size > 1) {
                            // Create oneOf for mixed types, but merge integer and number to avoid conflicts
                            const mergedTypes = new Set();
                            let hasNumber = false;
                            let hasInteger = false;
                            
                            uniqueTypes.forEach(type => {
                                if (type === 'integer') hasInteger = true;
                                else if (type === 'number') hasNumber = true;
                                else mergedTypes.add(type);
                            });
                            
                            // If both integer and number exist, use only number
                            if (hasInteger && hasNumber) {
                                mergedTypes.add('number');
                            } else if (hasInteger) {
                                mergedTypes.add('integer');
                            } else if (hasNumber) {
                                mergedTypes.add('number');
                            }
                            
                            propSchema.oneOf = Array.from(mergedTypes).map(type => {
                                const sampleValue = allValuesForProp.find(val => {
                                    const valType = getJsonType(val);
                                    // For number type, accept both integer and number values
                                    if (type === 'number') {
                                        return valType === 'number' || valType === 'integer';
                                    }
                                    return valType === type;
                                });
                                const typeSchema = { type };
                                if (schemaSettings.includeExamples && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('examples'))) {
                                    typeSchema.examples = [sampleValue];
                                }
                                return typeSchema;
                            });
                            delete propSchema.type;
                            delete propSchema.examples;
                            if (propSchema.default !== undefined) delete propSchema.default;
                        }
                    }
                }
            }

            if (schemaSettings.arrayValidation && (!schemaVersionInfo.customValidations || 
                (schemaVersionInfo.customValidations.includes('minItems') && schemaVersionInfo.customValidations.includes('uniqueItems')))) {
                currentSchemaNode.minItems = currentJson.length > 0 ? 1 : 0; 
                currentSchemaNode.uniqueItems = allElementsUnique(currentJson);
            }
            
            // Add additionalItems for draft-cs if specified
            if (currentVersionId === 'draft-cs' && schemaVersionInfo.customValidations.includes('additionalItems')) {
                currentSchemaNode.additionalItems = true;
            }
            
            // Add examples for arrays in draft-cs
            if (currentVersionId === 'draft-cs' && schemaSettings.includeExamples && 
                (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('examples'))) {
                currentSchemaNode.examples = [currentJson];
            }
        } else if (type !== 'null') { 
            // 'examples' is preferred over 'example' in Draft 07+
            if (schemaSettings.includeExamples && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('examples'))) 
                currentSchemaNode.examples = [currentJson];
                
            if (schemaSettings.includeDefaults && currentVersionId !== 'draft-cs' && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('default'))) 
                currentSchemaNode.default = currentJson;

            if (type === 'string') {
                if (schemaSettings.formatValidation && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('format'))) {
                    const format = detectStringFormat(currentJson);
                    if (format) currentSchemaNode.format = format;
                }
                if (schemaSettings.stringValidation && (!schemaVersionInfo.customValidations || 
                   (schemaVersionInfo.customValidations.includes('minLength') && schemaVersionInfo.customValidations.includes('maxLength')))) {
                     currentSchemaNode.minLength = currentJson.length > 0 ? 1 : 0; 
                     currentSchemaNode.maxLength = currentJson.length; 
                     if (currentSchemaNode.format === 'email') currentSchemaNode.pattern = "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$";
                     else if (currentSchemaNode.format === 'date') currentSchemaNode.pattern = "^\\d{4}-\\d{2}-\\d{2}$";
                }
            }
        } else if (type === 'null') {
            if (schemaSettings.includeExamples && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('examples'))) 
                currentSchemaNode.examples = [null];
                
            if (schemaSettings.includeDefaults && currentVersionId !== 'draft-cs' && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('default'))) 
                currentSchemaNode.default = null;
        }
    };

    buildPropertiesRecursive(json, schema);

    if (getJsonType(json) === 'array') {
        const rootItems = schema.items;
        const rootType = schema.type;
        const arrayValidations = {};
        if(schema.minItems !== undefined) arrayValidations.minItems = schema.minItems;
        if(schema.uniqueItems !== undefined) arrayValidations.uniqueItems = schema.uniqueItems;
        const rootDefault = schema.default; 
        const rootExamples = schema.examples;

        for (const key in schema) { delete schema[key]; }
        schema.$schema = schemaVersionInfo.url;
        
        // Add $id for draft-cs
        if (currentVersionId === 'draft-cs' && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('$id'))) {
          schema.$id = `http://example.com/example.json`;
        }
        
        if (schemaSettings.includeTitle && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('title'))) {
          if (currentVersionId === 'draft-cs') {
            schema.title = "Root Schema";
          } else {
            schema.title = "Generated Schema (Array Root)";
          }
        }
          
        if (schemaSettings.includeDescription && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('description'))) 
          schema.description = "Schema generated for a root JSON array";
          
        schema.type = rootType || 'array'; 
        
        if (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('items'))
          schema.items = rootItems || {};    
          
        // Only include array validations if they're in the custom validations list for draft-cs
        if (!schemaVersionInfo.customValidations || 
            (schemaVersionInfo.customValidations.includes('minItems') && schemaVersionInfo.customValidations.includes('uniqueItems'))) {
          Object.assign(schema, arrayValidations);
        }
        
        if (schemaSettings.includeDefaults && currentVersionId !== 'draft-cs' && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('default')) && typeof rootDefault !== 'undefined') 
          schema.default = rootDefault;
          
        if (schemaSettings.includeExamples && (!schemaVersionInfo.customValidations || schemaVersionInfo.customValidations.includes('examples')) && typeof rootExamples !== 'undefined') 
          schema.examples = rootExamples;

    } else if (getJsonType(json) !== 'object') { 
        delete schema.properties;
        delete schema.required;
        delete schema.additionalProperties;
        delete schema.minProperties;
        delete schema.maxProperties;
    }
    
    // For draft-cs, remove any properties not in the customValidations list
    if (schemaVersionInfo.customValidations) {
      const allowedProps = [...schemaVersionInfo.customValidations, '$schema', 'type']; // Always keep $schema and type
      Object.keys(schema).forEach(key => {
        if (!allowedProps.includes(key)) {
          delete schema[key];
        }
      });
    }
    
    return schema;
  };
  
  const generateSchemaHandler = () => {
    try {
      setError('');
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to generate schema.');
        setSchemaOutput(''); 
        return;
      }

      const lineCount = jsonInput.split('\n').length;
      if (lineCount > MAX_LINE_COUNT) {
        setError(`Input exceeds the maximum limit of ${MAX_LINE_COUNT.toLocaleString()} lines. Please reduce the size of your JSON.`);
        setSchemaOutput('');
        return;
      }

      const scrollPosition = window.scrollY;
      const mainContainer = document.querySelector('.json-schema-container');
      const containerHeight = mainContainer ? mainContainer.offsetHeight : null;
      
      const parsedJson = JSON.parse(jsonInput);
      const schemaResult = generateJsonSchema(parsedJson, schemaVersion);
      setSchemaOutput(JSON.stringify(schemaResult, null, 2));
      
      if (mainContainer && containerHeight) {
        mainContainer.style.minHeight = `${containerHeight}px`;
      }
      setTimeout(() => window.scrollTo(0, scrollPosition), 0);
    } catch (e) {
      setError('Error generating schema: ' + (e.message || 'Invalid JSON input.'));
      setSchemaOutput('');
    }
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        
        const lineCount = content.split('\n').length;
        if (lineCount > MAX_LINE_COUNT) {
          setError(`The uploaded file exceeds the maximum limit of ${MAX_LINE_COUNT.toLocaleString()} lines. Please upload a smaller file.`);
          return;
        }
        
        setJsonInput(content);
        setSchemaOutput(''); 
        setError('');
      };
      reader.readAsText(file);
    }
  };
  const handleUploadClick = () => { if (fileInputRef.current) fileInputRef.current.click(); };
  const loadExample = () => { 
    setJsonInput(JSON.stringify(exampleJson.person, null, 2)); 
    setSchemaOutput(''); setError(''); 
  };
  const downloadSchemaOutput = () => {
     if (!schemaOutput.trim()) { setError('Nothing to download. Please generate a schema first.'); return; }
     try {
      const blob = new Blob([schemaOutput], { type: 'application/json;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `generated_schema-${schemaVersion}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url); setError('');
    } catch (e) { setError('Error downloading schema: ' + e.message); }
  };
  const handleTempSettingChange = (setting) => setTempSettings(prev => ({ ...prev, [setting]: !prev[setting] }));
  const openOptionsModal = () => { setTempSettings({ ...schemaSettings }); setShowOptionsModal(true); };
  const closeOptionsModal = () => setShowOptionsModal(false);
  const applySettings = () => { 
    setSchemaSettings({ ...tempSettings }); 
    setShowOptionsModal(false); 
    if (jsonInput.trim()) generateSchemaHandler();
  };
  const copyToClipboard = () => {
    if (!schemaOutput.trim()) { setError('Nothing to copy. Please generate a schema first.'); return; }
    navigator.clipboard.writeText(schemaOutput).then(() => {
      setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); setError('');
    }).catch(err => setError('Failed to copy schema: ' + err));
  };

  const generatePostmanTest = () => {
    if (!schemaOutput.trim()) { setError('Please generate a schema first.'); return; }
    try {
      const postmanScript = `const actualResponse = pm.response.json();
const Ajv = require('ajv');
const ajv = new Ajv();

const schema = ${schemaOutput};

const validate = ajv.compile(schema);

pm.test('Schema Validation', () => {
    const valid = validate(actualResponse);
    if (!valid) console.log(validate.errors);
    pm.expect(valid).to.be.true;
});`;

      setSchemaOutput(postmanScript);
      setError('');
    } catch (e) { setError('Error generating Postman test: ' + e.message); }
  };

  const handleInputScroll = () => {
    if (!isOutputScrolling && schemaOutputRef.current && jsonInputRef.current) {
      setIsInputScrolling(true);
      const inputScrollTop = jsonInputRef.current.scrollTop;
      const inputScrollHeight = jsonInputRef.current.scrollHeight;
      const inputClientHeight = jsonInputRef.current.clientHeight;
      
      const outputScrollHeight = schemaOutputRef.current.scrollHeight;
      const outputClientHeight = schemaOutputRef.current.clientHeight;
      
      const scrollRatio = inputScrollTop / (inputScrollHeight - inputClientHeight || 1);
      const outputScrollTop = scrollRatio * (outputScrollHeight - outputClientHeight || 1);
      
      schemaOutputRef.current.scrollTop = outputScrollTop;
      
      if (jsonInputLineNumbersRef.current) {
        jsonInputLineNumbersRef.current.scrollTop = inputScrollTop;
      }
      
      setTimeout(() => setIsInputScrolling(false), 50);
    }
  };

  const handleOutputScroll = () => {
    if (!isInputScrolling && jsonInputRef.current && schemaOutputRef.current) {
      setIsOutputScrolling(true);
      const outputScrollTop = schemaOutputRef.current.scrollTop;
      const outputScrollHeight = schemaOutputRef.current.scrollHeight;
      const outputClientHeight = schemaOutputRef.current.clientHeight;
      
      const inputScrollHeight = jsonInputRef.current.scrollHeight;
      const inputClientHeight = jsonInputRef.current.clientHeight;
      
      const scrollRatio = outputScrollTop / (outputScrollHeight - outputClientHeight || 1);
      const inputScrollTop = scrollRatio * (inputScrollHeight - inputClientHeight || 1);
      
      jsonInputRef.current.scrollTop = inputScrollTop;
      
      if (schemaOutputLineNumbersRef.current) {
        schemaOutputLineNumbersRef.current.scrollTop = outputScrollTop;
      }
      
      setTimeout(() => setIsOutputScrolling(false), 50);
    }
  };

  const renderLineNumbers = (text, lineNumbersRefParam) => {
    if (!text) return null;
    const lines = text.split('\n');
    
    const totalLines = lines.length;
    const lineNumWidth = totalLines > 999 ? 50 : totalLines > 99 ? 40 : 30;

    return (
      <div
        ref={lineNumbersRefParam}
        style={{
          position: 'absolute', left: '0px', top: '0px',
          width: `${lineNumWidth}px`, textAlign: 'right', color: '#6e7681',
          userSelect: 'none', fontFamily: '"Consolas", "Monaco", monospace',
          fontSize: '13px', fontWeight: '400', lineHeight: '1.5',
          height: '100%', overflow: 'hidden', boxSizing: 'border-box',
          borderRight: '1px solid #e1e4e8', backgroundColor: '#f6f8fa',
          paddingTop: '10px', paddingBottom: '10px', zIndex: 1
        }}
      >
        {lines.map((_, i) => (
          <div key={i} style={{ 
            lineHeight: '1.5', boxSizing: 'border-box', paddingRight: '10px',
            position: 'relative', color: (i + 1) % 5 === 0 ? '#24292e' : '#6e7681',
            fontWeight: (i + 1) % 10 === 0 ? '600' : '400', height: '21px'
          }}>
            {i + 1}
          </div>
        ))}
      </div>
    );
  };

  const controlButtonStyle = {
    width: '100%', padding: '10px', margin: '5px 0', 
    color: 'white', border: 'none', borderRadius: '4px', 
    cursor: 'pointer', fontSize: '14px', boxSizing: 'border-box',
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setJsonInput(newValue);
    
    if (error && error.includes('maximum limit')) {
      setError('');
    }
  };

  // JSON-LD structured data for the Schema Generator
  const schemaGeneratorJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "JSON Schema Generator",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Free online tool to generate JSON Schema from JSON data. Create schemas for various drafts including 2020-12, Draft-07, and custom schemas.",
    "url": `${SITE_DOMAIN}/json-to-schema`,
    "featureList": "Multiple schema versions, customizable validation options, format detection, example generation",
    "keywords": "JSON schema generator, JSON validation, JSON schema, schema from example, draft-2020-12",
    "publisher": {
      "@type": "Organization",
      "name": "JSON Tools",
      "url": SITE_DOMAIN
    }
  };

  return (
    <>
      <SEO 
        title="JSON to Schema Generator | Convert JSON to Schema Draft 2020-12, Draft-07"
        description="Free online tool to generate JSON Schema from JSON data. Create validation schemas with custom options. Supports Draft 2020-12, Draft-07 and other JSON Schema versions."
        keywords={["JSON to schema", "JSON schema generator", "generate JSON schema", "create schema from JSON", "JSON Schema 2020-12", "JSON Schema Draft-07", "validation schema"]}
        canonicalUrl={`${SITE_DOMAIN}/json-to-schema`}
        structuredData={schemaGeneratorJsonLd}
      />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 20px 0' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>JSON to Schema Generator</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Generate JSON Schema from your JSON data. Supports Draft 2020-12, Draft-07, and other schema versions.
        </p>
      </div>
      
      <div
        className="json-schema-container responsive-main-container"
        style={{
          display: 'flex', height: 'calc(100vh - 150px)', maxHeight: '800px',
          minHeight: '500px', width: '90%', margin: '0 auto', padding: '20px',
          gap: '40px', boxSizing: 'border-box',
        }}
      >
        <div className="responsive-panel" style={{ 
          flex: 1, position: 'relative', display: 'flex', 
          flexDirection: 'column', minWidth: 0,
          maxHeight: '100%', overflow: 'hidden'
        }}>
          <div style={{ 
            position: 'relative', flex: 1, display: 'flex', 
            flexDirection: 'column', height: '100%', overflow: 'hidden'
          }}>
            {renderLineNumbers(jsonInput, jsonInputLineNumbersRef)}
            <TextareaWithLines
              value={jsonInput}
              onChange={handleInputChange}
              placeholder={"Enter your JSON data here..."}
              textareaRef={jsonInputRef}
              lineNumbersRef={jsonInputLineNumbersRef}
              onScroll={handleInputScroll}
              style={{ resize: 'none', overflowY: 'auto' }}
              textareaProps={{ className: 'responsive-textarea' }}
              lineNumbersRefClassName="responsive-linenumbers"
            />
          </div>
        </div>

        <div className="responsive-controls" style={{
          width: '220px', flexShrink: 0, display: 'flex', 
          flexDirection: 'column', alignItems: 'center', 
          padding: '0 10px', boxSizing: 'border-box',
        }}>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} accept=".json,.txt"/>
          <button onClick={handleUploadClick} style={{ ...controlButtonStyle, backgroundColor: '#00BFA5' }}>Upload JSON</button>
          <button onClick={loadExample} style={{ ...controlButtonStyle, backgroundColor: '#00BFA5' }}>Load Example</button>
          <select
            value={schemaVersion}
            onChange={(e) => setSchemaVersion(e.target.value)}
            style={{ width: '100%', padding: '10px', margin: '5px 0', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
          >
            {schemaVersions.map(version => (<option key={version.id} value={version.id}>{version.name}</option>))}
          </select>
          <button onClick={openOptionsModal} style={{ ...controlButtonStyle, backgroundColor: '#00BFA5' }}>Schema Options</button>
          <div style={{ margin: '10px 0' }} />
          <button onClick={generateSchemaHandler} style={{ ...controlButtonStyle, backgroundColor: '#007bff', fontWeight: 'bold' }}>Generate Schema</button>
          <button onClick={downloadSchemaOutput} style={{ ...controlButtonStyle, backgroundColor: '#28a745' }}>Download Schema</button>
          <button onClick={generatePostmanTest} style={{ ...controlButtonStyle, backgroundColor: '#ff6b35' }}>Create Postman Schema Test</button>
          {error && (
            <div style={{ padding: '10px', marginTop: '10px', color: 'white', backgroundColor: '#dc3545', textAlign: 'center', width: '100%', fontWeight: 'bold', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', wordBreak: 'break-word' }}>
              {error}
            </div>
          )}
        </div>

        <div className="responsive-panel" style={{ 
          flex: 1, position: 'relative', display: 'flex', 
          flexDirection: 'column', minWidth: 0,
          maxHeight: '100%', overflow: 'hidden'
        }}>
          <div style={{ 
            position: 'relative', flex: 1, display: 'flex', 
            flexDirection: 'column', height: '100%', overflow: 'hidden'
          }}>
            {schemaOutput && (
              <button onClick={copyToClipboard} style={{
                  position: 'absolute', top: '10px', right: '10px', padding: '5px 10px',
                  backgroundColor: copySuccess ? '#4CAF50' : '#00BFA5', color: 'white',
                  border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                  zIndex: 10, transition: 'background-color 0.3s'
              }}>
                {copySuccess ? 'Copied!' : 'Copy Schema'}
              </button>
            )}
            {renderLineNumbers(schemaOutput, schemaOutputLineNumbersRef)}
            <PreWithLines
              value={schemaOutput}
              preRef={schemaOutputRef}
              lineNumbersRef={schemaOutputLineNumbersRef}
              onScroll={handleOutputScroll}
              style={{ backgroundColor: '#fafafa', whiteSpace: 'pre-wrap', wordBreak: 'break-all', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, maxHeight: '100%' }}
              preProps={{ className: 'responsive-pre' }}
              lineNumbersRefClassName="responsive-linenumbers"
            />
          </div>
        </div>
      </div>

      {showOptionsModal && (
         <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0, 0, 0, 0.6)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(3px)'
        }}>
          <div style={{
            background: 'white', padding: '25px', borderRadius: '8px',
            width: '500px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e9ecef', paddingBottom: '15px' }}>
              <h3 style={{ margin: 0, fontSize: '1.3em', color: '#333' }}>Schema Generation Options</h3>
              <button onClick={closeOptionsModal} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', lineHeight: '1', color: '#888', padding: '0 5px' }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <h4 style={{ marginTop: '0', marginBottom: '12px', fontSize: '1em', color: '#555', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>Basic Settings</h4>
                {Object.keys(initialSchemaSettings)
                  .filter(key => ['includeTitle', 'includeDescription', 'required', 'includeDefaults', 'includeExamples', 'strictTypes', 'additionalProperties'].includes(key))
                  .map(key => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', margin: '10px 0' }}>
                      <input type="checkbox" id={`jts_modal_${key}`} checked={tempSettings[key]} onChange={() => handleTempSettingChange(key)} style={{ marginRight: '10px', transform: 'scale(1.1)', cursor: 'pointer' }} />
                      <label htmlFor={`jts_modal_${key}`} style={{ fontSize: '0.95em', color: '#444', cursor: 'pointer' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                    </div>
                ))}
              </div>
              <div>
                <h4 style={{ marginTop: '0', marginBottom: '12px', fontSize: '1em', color: '#555', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>Validation Settings</h4>
                {Object.keys(initialSchemaSettings)
                  .filter(key => ['arrayValidation', 'stringValidation', 'numberValidation', 'formatValidation'].includes(key))
                  .map(key => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', margin: '10px 0' }}>
                      <input type="checkbox" id={`jts_modal_${key}`} checked={tempSettings[key]} onChange={() => handleTempSettingChange(key)} style={{ marginRight: '10px', transform: 'scale(1.1)', cursor: 'pointer' }} />
                      <label htmlFor={`jts_modal_${key}`} style={{ fontSize: '0.95em', color: '#444', cursor: 'pointer' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                    </div>
                ))}
                <h4 style={{ marginTop: '20px', marginBottom: '12px', fontSize: '1em', color: '#555', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>Advanced Settings</h4>
                {Object.keys(initialSchemaSettings)
                  .filter(key => ['enumValidation', 'minMaxProperties', 'patternProperties', 'dependencies'].includes(key))
                  .map(key => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', margin: '10px 0' }}>
                      <input type="checkbox" id={`jts_modal_${key}`} checked={tempSettings[key]} onChange={() => handleTempSettingChange(key)} style={{ marginRight: '10px', transform: 'scale(1.1)', cursor: 'pointer' }} />
                      <label htmlFor={`jts_modal_${key}`} style={{ fontSize: '0.95em', color: '#444', cursor: 'pointer' }}>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        {key === 'dependencies' && schemaVersion === 'draft-2020-12' && <span style={{fontSize: '0.8em', color: '#777'}}> (Deprecated)</span>}
                      </label>
                    </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '25px', paddingTop: '20px', borderTop: '1px solid #e9ecef' }}>
              <button onClick={closeOptionsModal} style={{ padding: '10px 18px', border: '1px solid #ccc', borderRadius: '4px', background: '#f8f9fa', cursor: 'pointer', fontSize: '0.95em', color: '#333' }}>Cancel</button>
              <button onClick={applySettings} style={{ padding: '10px 18px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.95em', fontWeight: '500' }}>Apply Settings</button>
            </div>
          </div>
        </div>
      )}
      
      <div style={{ maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>About JSON to Schema Generator</h2>
        <p style={{ marginBottom: '15px' }}>
          JSON Schema is a powerful tool for validating the structure of JSON data. It allows you to define the expected format, data types, and constraints for your JSON documents. However, creating JSON Schema manually can be time-consuming and error-prone.
        </p>
        <p style={{ marginBottom: '15px' }}>
          Our <strong>JSON to Schema Generator</strong> automatically creates a JSON Schema from your sample JSON data. This makes it easy to:
        </p>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>Document the expected format of your API requests and responses</li>
          <li style={{ marginBottom: '8px' }}>Set up validation for user-submitted data</li>
          <li style={{ marginBottom: '8px' }}>Create consistent data models across your application</li>
          <li style={{ marginBottom: '8px' }}>Generate code or documentation from your schema</li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Key Features</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Multiple Schema Versions</strong>: Support for various JSON Schema drafts (2020-12, 07, 06, 04, 03)</li>
          <li style={{ marginBottom: '8px' }}><strong>Customizable Options</strong>: Fine-tune your schema with settings for required fields, validation rules, and more</li>
          <li style={{ marginBottom: '8px' }}><strong>Type Detection</strong>: Automatically detects and assigns correct data types</li>
          <li style={{ marginBottom: '8px' }}><strong>Format Recognition</strong>: Identifies common formats like dates, emails, and IP addresses</li>
          <li style={{ marginBottom: '8px' }}><strong>Array Validation</strong>: Handles arrays with consistent or mixed item types</li>
          <li style={{ marginBottom: '8px' }}><strong>Nested Objects</strong>: Properly processes complex nested JSON structures</li>
        </ul>
        <p style={{ marginBottom: '15px' }}>
          Our tool processes everything in your browser, ensuring your data remains private and secure. It can handle complex JSON structures while still producing clean, maintainable schemas.
        </p>
        <p>
          Whether you're an API developer, a QA engineer setting up test validations, or a database designer creating document schemas, our JSON to Schema Generator saves you time and ensures your schemas are accurate and comprehensive.
        </p>
      </div>
      
    </>
  );
};

export default JsonToSchemaPage;